import { isURL } from "validator";

// Validación de los links de redes sociales y del sitio web del perfil.
//
// Cada red acepta dos formas, y las dos terminan guardadas como link:
//   - el link del perfil, de ESA red ("instagram.com/alfirifi", con o sin
//     https://). Un link de otra red en el campo equivocado se rechaza.
//   - el usuario con arroba ("@alfirifi"), que se convierte al link del
//     perfil. Es como la mayoría comparte su cuenta, y pedir el link
//     completo hacía que muchos lo dejaran vacío o pegaran cualquier cosa.
//
// El sitio web es libre —cualquier dominio— pero tiene que ser un link: un
// "@usuario" suelto ahí no dice de qué red es.
//
// Espejo en el backend: Backend/src/utils/redesSociales.js. Si cambiás una
// regla acá, cambiala allá también: el servidor es el que la sostiene.

export const REDES = {
  instagram: {
    nombre: "Instagram",
    dominios: ["instagram.com"],
    usuario: /^[A-Za-z0-9._]{1,30}$/,
    armarUrl: (usuario) => `https://www.instagram.com/${usuario}`,
    ejemplo: "instagram.com/tuusuario o @tuusuario",
  },
  tiktok: {
    nombre: "TikTok",
    dominios: ["tiktok.com"],
    usuario: /^[A-Za-z0-9._]{2,24}$/,
    armarUrl: (usuario) => `https://www.tiktok.com/@${usuario}`,
    ejemplo: "tiktok.com/@tuusuario o @tuusuario",
  },
  twitter: {
    nombre: "X (Twitter)",
    dominios: ["x.com", "twitter.com"],
    usuario: /^[A-Za-z0-9_]{1,15}$/,
    armarUrl: (usuario) => `https://x.com/${usuario}`,
    ejemplo: "x.com/tuusuario o @tuusuario",
  },
  facebook: {
    nombre: "Facebook",
    dominios: ["facebook.com", "fb.com"],
    usuario: /^[A-Za-z0-9.]{5,50}$/,
    armarUrl: (usuario) => `https://www.facebook.com/${usuario}`,
    ejemplo: "facebook.com/tupagina o @tupagina",
  },
};

export const EJEMPLO_SITIO_WEB = "https://tusitio.com";

// Texto → URL, agregando https:// si falta. Null si no es un link.
function aUrl(texto) {
  if (/\s/.test(texto)) return null;
  const conProtocolo = /^https?:\/\//i.test(texto) ? texto : `https://${texto}`;
  if (
    !isURL(conProtocolo, {
      protocols: ["http", "https"],
      require_protocol: true,
      require_tld: true,
    })
  )
    return null;
  try {
    return new URL(conProtocolo);
  } catch (e) {
    return null;
  }
}

// El dominio exacto o un subdominio (www., m.). "instagram.com.otro.com" o
// "noinstagram.com" no pasan.
function esDelDominio(hostname, dominios) {
  const host = hostname.toLowerCase();
  return dominios.some((d) => host === d || host.endsWith(`.${d}`));
}

// Devuelve { ok, valor, error }. `valor` es lo que se guarda; vacío es
// válido porque ninguna red es obligatoria por sí sola.
export function normalizarRed(red, entrada) {
  const def = REDES[red];
  const texto = String(entrada ?? "").trim();
  if (!texto) return { ok: true, valor: "" };

  if (texto.startsWith("@")) {
    const usuario = texto.slice(1);
    if (!def.usuario.test(usuario))
      return {
        ok: false,
        error: `"${texto}" no es un usuario de ${def.nombre} válido.`,
      };
    return { ok: true, valor: def.armarUrl(usuario), desdeArroba: true };
  }

  const url = aUrl(texto);
  if (!url)
    return {
      ok: false,
      error: `Pega el link de tu perfil de ${def.nombre} o tu usuario con @ (ej: ${def.ejemplo}).`,
    };
  if (!esDelDominio(url.hostname, def.dominios))
    return { ok: false, error: `Ese link no es de ${def.nombre}.` };
  if (url.pathname.replace(/\/+$/, "") === "")
    return {
      ok: false,
      error: `Al link le falta tu usuario de ${def.nombre} (ej: ${def.ejemplo}).`,
    };
  return { ok: true, valor: url.toString() };
}

// Primer tramo de la ruta que NO es un usuario ("instagram.com/p/…" es una
// publicación, "facebook.com/profile.php?id=…" no tiene usuario).
const RUTAS_QUE_NO_SON_USUARIO = [
  "p",
  "reel",
  "reels",
  "stories",
  "explore",
  "tv",
  "share",
  "profile.php",
  "pages",
  "people",
  "groups",
  "intent",
  "home",
  "i",
];

// El usuario (sin @) de un link o "@usuario" de una red, para mencionar al
// artista al compartir. Tolera lo que haya guardado: los perfiles nuevos
// guardan el link normalizado, pero las fichas de roster y los perfiles
// viejos pueden tener el link con parámetros, el "@usuario" o el usuario
// solo. Null si no se puede sacar uno válido: es mejor no mencionar que
// mencionar a otra cuenta.
export function usuarioDeRed(red, valor) {
  const def = REDES[red];
  const texto = String(valor ?? "").trim();
  if (!def || !texto) return null;

  const valido = (usuario) =>
    usuario && def.usuario.test(usuario) ? usuario : null;

  if (texto.startsWith("@")) return valido(texto.slice(1));
  // Sin "/" ni ":" es el usuario solo ("alfi.rifi"), salvo que sea el
  // dominio pelado de la red ("instagram.com"), que no dice de quién es.
  if (!/[/:]/.test(texto))
    return esDelDominio(texto, def.dominios) ? null : valido(texto);

  const url = aUrl(texto);
  if (!url || !esDelDominio(url.hostname, def.dominios)) return null;
  const tramo = decodeURIComponent(url.pathname.split("/").filter(Boolean)[0] || "");
  if (RUTAS_QUE_NO_SON_USUARIO.includes(tramo.toLowerCase())) return null;
  return valido(tramo.replace(/^@/, ""));
}

export function normalizarSitioWeb(entrada) {
  const texto = String(entrada ?? "").trim();
  if (!texto) return { ok: true, valor: "" };
  if (texto.startsWith("@"))
    return {
      ok: false,
      error: `El sitio web tiene que ser un link (ej: ${EJEMPLO_SITIO_WEB}), no un usuario con @.`,
    };
  const url = aUrl(texto);
  if (!url)
    return {
      ok: false,
      error: `Ingresa un link válido (ej: ${EJEMPLO_SITIO_WEB}).`,
    };
  return { ok: true, valor: url.toString() };
}

// Texto de ayuda para debajo del campo: el error si lo hay, a dónde va a
// apuntar un @usuario una vez convertido, o el ejemplo si está vacío.
export function ayudaCampo(resultado, ejemplo) {
  if (!resultado.ok) return resultado.error;
  if (resultado.desdeArroba) return `Se guardará como ${resultado.valor}`;
  if (!resultado.valor) return `Ej: ${ejemplo}`;
  return " ";
}
